import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from "@angular/common/http";
import { Observable, throwError, timer } from "rxjs";
import { catchError, retry, tap, switchMap } from 'rxjs/operators';
import { StorageService } from "../services/storage.service";
import { SessionService } from "../services/session.service";
import { ErrorStateService } from "../services/error-state.service";
import { ReauthService } from "../services/reauth.service";
import { environment } from "src/environments/environment";

/**
 * AuthorizationInterceptor - Gestione centralizzata delle richieste HTTP
 * 
 * Funzionalità:
 * - Aggiunge token di autorizzazione
 * - Retry automatico con exponential backoff per errori di rete
 * - Gestione errori 401/403
 * - Notifica ErrorStateService per errori persistenti
 */
@Injectable()
export class AuthorizationInterceptor implements HttpInterceptor {
  
  // Configurazione retry
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 1000; // Base delay, raddoppia ad ogni retry
  
  // URL da escludere dal retry (es. login, logout)
  private readonly EXCLUDE_RETRY_URLS = ['/login', '/logout'];

  constructor(
    private storageService: StorageService,
    private sessionService: SessionService,
    private errorStateService: ErrorStateService,
    private reauthService: ReauthService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    const token = this.storageService.getToken();

    // Only add the authorization header if we have a token
    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    // ========================================================================
    // AUDIT META ENRICHMENT (Fase B — ExternalData/PIANO_AUDIT_FRAMEWORK.md)
    // Iniezione di _traceId / _sessionId / _sourceVersion nel BODY (non headers,
    // per evitare preflight CORS breaks). Solo per POST con body object puro:
    // - FormData upload file: skip
    // - GET/DELETE: nessun body, skip
    // - Body stringa/array/null: skip (protezione generica)
    // Safe: niente throw mai, niente .catch bloccante. Se qualcosa va male il
    // request originale prosegue senza enrichment.
    // ========================================================================
    request = this._enrichBodyWithAuditMeta(request);

    // Determina se questa richiesta deve avere retry
    const shouldRetry = this.shouldRetryRequest(request);

    // Process the request with retry logic
    return next.handle(request).pipe(
      // Retry automatico per errori di rete (con exponential backoff)
      retry({
        count: shouldRetry ? this.MAX_RETRIES : 0,
        delay: (error, retryCount) => {
          // Retry solo per errori di rete, non per errori HTTP (4xx, 5xx)
          if (!this.isRetryableError(error)) {
            throw error;
          }
          
          const delayMs = this.RETRY_DELAY_MS * Math.pow(2, retryCount - 1);
          console.log(`🔄 Interceptor: Retry ${retryCount}/${this.MAX_RETRIES} in ${delayMs}ms - ${request.url}`);
          return timer(delayMs);
        }
      }),
      
      // Registra successo per resettare il contatore errori
      tap(() => {
        this.errorStateService.registerSuccess();
      }),
      
      // Gestione errori
      catchError((error: HttpErrorResponse) => {
        // 401 → ri-autenticazione IN-PLACE (niente reload, niente lavoro perso).
        // Single-flight nel ReauthService: 401 concorrenti aprono UNA sola modale e
        // vengono tutte rigiocate col nuovo token. Rotte escluse (login/recover) e
        // richieste già ritentate (X-Reauth-Retry) NON entrano qui → fallback logout.
        if (error.status === 401 && this.isReauthEligible(request)) {
          return this.reauthService.requestReauth().pipe(
            switchMap((newToken) => next.handle(this.addRetryToken(request, newToken)))
          );
        }
        return this.handleError(error, request);
      })
    );
  }

  /**
   * Una richiesta è eleggibile alla ri-autenticazione in-place se NON è già stata
   * ritentata una volta (evita loop infiniti) e NON è una rotta di auth pubblica
   * (login/recover: gestiscono i propri errori e la modale stessa chiama /login).
   */
  private isReauthEligible(request: HttpRequest<any>): boolean {
    // Già ritentata una volta col token nuovo → non riaprire (anti-loop).
    if (request.headers.has('X-Reauth-Retry')) { return false; }
    // Nessun token in storage → NON è una sessione "scaduta" ma "assente"
    // (logout in corso o richiesta pre-login): niente modale, si ricade sul
    // logout/redirect. La re-auth serve solo a chi aveva un token ora rifiutato.
    if (!this.storageService.getToken()) { return false; }
    const url = request.url.toLowerCase();
    return !['/login', '/recover-credentials', '/logout'].some(u => url.includes(u));
  }

  /**
   * Clona la richiesta fallita col nuovo token e un marcatore X-Reauth-Retry: se anche
   * il token nuovo venisse rifiutato (secondo 401), la richiesta NON è più eleggibile
   * → niente loop, si ricade sul logout.
   */
  private addRetryToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Reauth-Retry': '1'
      }
    });
  }

  /**
   * Arricchisce il body delle richieste POST con i campi di audit
   * (_traceId, _sessionId, _sourceVersion). Mai throw: se qualcosa fallisce
   * ritorna la request originale senza enrichment.
   *
   * Condizioni per arricchire:
   * - Metodo POST
   * - Body è un oggetto "puro" (non FormData, non Array, non primitivo, non null)
   */
  private _enrichBodyWithAuditMeta(request: HttpRequest<any>): HttpRequest<any> {
    try {
      if (request.method !== 'POST') return request;
      const body = request.body;
      if (!body) return request;
      if (body instanceof FormData) return request;
      if (typeof body !== 'object') return request;
      if (Array.isArray(body)) return request;

      const user = this.storageService.getUserStorage();
      const sessionId = user?.sessionId || null;
      const traceId = this._generateUuid();
      const sourceVersion = (environment as any).APP_VERSION || null;

      return request.clone({
        body: {
          ...body,
          _traceId: traceId,
          _sessionId: sessionId,
          _sourceVersion: sourceVersion
        }
      });
    } catch (e) {
      // Swallow: niente può bloccare il request flow per un errore di enrichment
      console.log('⚠️ [Interceptor] _enrichBodyWithAuditMeta skipped:', e);
      return request;
    }
  }

  /**
   * Genera un UUID v4 usando crypto.randomUUID() dove disponibile, con fallback.
   */
  private _generateUuid(): string {
    try {
      if (typeof (window as any).crypto?.randomUUID === 'function') {
        return (window as any).crypto.randomUUID();
      }
    } catch (_) { /* fallback sotto */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Determina se la richiesta deve avere retry automatico
   */
  private shouldRetryRequest(request: HttpRequest<any>): boolean {
    // Escludi URL specifici dal retry
    const url = request.url.toLowerCase();
    return !this.EXCLUDE_RETRY_URLS.some(excludeUrl => url.includes(excludeUrl));
  }

  /**
   * Determina se l'errore è retryable (errori di rete, non HTTP)
   */
  private isRetryableError(error: HttpErrorResponse): boolean {
    // Status 0 = errore di rete (no response)
    // Status >= 500 = errore server (potenzialmente temporaneo)
    return error.status === 0 || error.status >= 500;
  }

  /**
   * Gestisce gli errori HTTP
   */
  private handleError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    // Determina il tipo di errore
    const errorType = this.getErrorType(error);
    
    // Handle 401 Unauthorized errors (expired or invalid token)
    if (error.status === 401) {
      console.log('🔐 Interceptor: 401 Unauthorized - invalidating session');
      this.sessionService.handleUnauthorized();
    }
    // Handle 403 Forbidden (token valido ma permessi insufficienti)
    else if (error.status === 403) {
      console.log('🔐 Interceptor: 403 Forbidden - access denied');
    }
    // Errori di rete o server - notifica ErrorStateService
    // NOTA: Non notificare per URL esclusi (login/logout) - gestiti localmente
    else if (this.isRetryableError(error) && this.shouldRetryRequest(request)) {
      console.log(`🔴 Interceptor: ${errorType} error after all retries - ${request.url}`);
      this.errorStateService.registerFailure(errorType, error.message || 'Connection error');
    }
    
    // Propagate the error
    return throwError(() => error);
  }

  /**
   * Determina il tipo di errore per la UI
   */
  private getErrorType(error: HttpErrorResponse): 'network' | 'server' | 'timeout' | 'unknown' {
    if (error.status === 0) {
      // Controlla se è un timeout
      if (error.message?.toLowerCase().includes('timeout')) {
        return 'timeout';
      }
      return 'network';
    }
    if (error.status >= 500) {
      return 'server';
    }
    return 'unknown';
  }
}